import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Car, Calculator, Shield } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './CarInsuranceCalculatorPage/components/CalculationForm';
import ResultsOverview from './CarInsuranceCalculatorPage/components/ResultsOverview';
import PremiumBreakdown from './CarInsuranceCalculatorPage/components/PremiumBreakdown';
import QuickAdjustmentSidebar from './CarInsuranceCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './CarInsuranceCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './CarInsuranceCalculatorPage/components/TipsSidebar';
import InfoContent from './CarInsuranceCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  carType: 'passenger',
  enginePower: '100',
  regionCode: '77', // Москва
  driverAge: '30',
  drivingExperience: '10',
  multiDrive: false, // Мультидрайв (без ограничения водителей)
  drivers: [
    { age: '30', experience: '10', noAccidents: true } // Первый водитель по умолчанию
  ],
  usagePeriod: '12', // Период использования в месяцах
  hasAccidents: false, // Были ДТП за прошлый период
  accidentsCount: '0',
  hasDiscounts: false, // Есть скидки
  kbmDiscount: '1', // КБМ (коэффициент бонус-малус)
  hasSeasonality: false, // Сезонность использования
  seasonalityMonths: ['6', '7', '8'], // Летние месяцы
  carAgeYears: '5', // Возраст автомобиля
  includeAdditionalOptions: false, // Включение дополнительных опций
  additionalOptions: {
    extendedCoverage: false, // Расширенное покрытие
    noDeductible: false, // Без франшизы
    evacuationService: false, // Услуги эвакуации
    rentalCar: false, // Аренда автомобиля во время ремонта
    remotePolicyIssue: false // Дистанционное оформление полиса
  }
};

// Определение типов транспортных средств
export const carTypes = [
  { id: 'passenger', name: 'Легковой автомобиль' },
  { id: 'truck', name: 'Грузовой автомобиль' },
  { id: 'bus', name: 'Автобус' },
  { id: 'motorcycle', name: 'Мотоцикл' },
  { id: 'tractor', name: 'Трактор и спецтехника' },
  { id: 'trailer', name: 'Прицеп' }
];

// Определение коэффициентов бонус-малус (КБМ)
export const kbmOptions = [
  { value: '0.5', label: 'КБМ 0.5 (Класс 13, максимальная скидка 50%)' },
  { value: '0.54', label: 'КБМ 0.54 (Класс 12, скидка 46%)' },
  { value: '0.59', label: 'КБМ 0.59 (Класс 11, скидка 41%)' },
  { value: '0.63', label: 'КБМ 0.63 (Класс 10, скидка 37%)' },
  { value: '0.68', label: 'КБМ 0.68 (Класс 9, скидка 32%)' },
  { value: '0.74', label: 'КБМ 0.74 (Класс 8, скидка 26%)' },
  { value: '0.8', label: 'КБМ 0.8 (Класс 7, скидка 20%)' },
  { value: '0.86', label: 'КБМ 0.86 (Класс 6, скидка 14%)' },
  { value: '0.91', label: 'КБМ 0.91 (Класс 5, скидка 9%)' },
  { value: '0.95', label: 'КБМ 0.95 (Класс 4, скидка 5%)' },
  { value: '1', label: 'КБМ 1.0 (Класс 3, базовый)' },
  { value: '1.17', label: 'КБМ 1.17 (Класс 2, надбавка 17%)' },
  { value: '1.4', label: 'КБМ 1.4 (Класс М, надбавка 40%)' },
  { value: '1.55', label: 'КБМ 1.55 (Класс 0, надбавка 55%)' },
  { value: '2.45', label: 'КБМ 2.45 (Класс -1, надбавка 145%)' },
  { value: '2.45', label: 'КБМ 2.45 (Класс -2, надбавка 145%)' },
  { value: '2.45', label: 'КБМ 2.45 (Класс -3, надбавка 145%)' }
];

// Список популярных регионов с коэффициентами
export const popularRegions = [
  { code: '77', name: 'Москва', factor: 1.9 },
  { code: '78', name: 'Санкт-Петербург', factor: 1.8 },
  { code: '50', name: 'Московская область', factor: 1.7 },
  { code: '23', name: 'Краснодарский край', factor: 1.3 },
  { code: '16', name: 'Республика Татарстан', factor: 1.1 },
  { code: '66', name: 'Свердловская область', factor: 1.0 },
  { code: '54', name: 'Новосибирская область', factor: 1.0 },
  { code: '86', name: 'ХМАО-Югра', factor: 1.0 },
  { code: '02', name: 'Республика Башкортостан', factor: 0.9 },
  { code: '74', name: 'Челябинская область', factor: 0.9 }
];

/**
 * CarInsuranceCalculatorPage Component - Полный калькулятор ОСАГО с различными параметрами.
 */
const CarInsuranceCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('car-insurance-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор ОСАГО | Gensio';
  const metaDescription = tool?.metaDescription || 'Точный расчет стоимости полиса ОСАГО онлайн: учёт всех коэффициентов, регионов, водительского стажа, КБМ и других параметров. Актуальные тарифы 2025 года по законодательству РФ.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedInsuranceCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved insurance calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'passenger':
          newState.carType = 'passenger';
          break;
        case 'truck':
          newState.carType = 'truck';
          break;
        case 'motorcycle':
          newState.carType = 'motorcycle';
          break;
        case 'multi-driver':
          newState.multiDrive = true;
          break;
        case 'young-driver':
          newState.driverAge = '20';
          newState.drivingExperience = '1';
          newState.drivers = [{ age: '20', experience: '1', noAccidents: true }];
          break;
        case 'seasonal':
          newState.hasSeasonality = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setBreakdown(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `ОСАГО для ${getCarTypeName(formData.carType)}, ${formData.enginePower} л.с.`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      breakdown: { ...breakdown }
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedInsuranceCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedInsuranceCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      setBreakdown(calcToLoad.breakdown);
    }
  };

  // Вспомогательная функция для получения названия типа автомобиля
  const getCarTypeName = (typeId) => {
    const found = carTypes.find(type => type.id === typeId);
    return found ? found.name : 'автомобиля';
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Shield className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор ОСАГО'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              breakdown={breakdown}
              setBreakdown={setBreakdown}
              carTypes={carTypes}
              popularRegions={popularRegions}
              kbmOptions={kbmOptions}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                
                {breakdown && <PremiumBreakdown breakdown={breakdown} />}
              </>
            )}

            {/* --- Информационный контент об ОСАГО --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              popularRegions={popularRegions}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Tables */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default CarInsuranceCalculatorPage;